import React, { useState, useEffect } from 'react';
import { Play, Youtube, Eye, Loader2 } from 'lucide-react';

const VideoSection = ({ 
  title = "Learn More About Your Rights",
  subtitle = "Watch these videos to understand how I can help you with your injury case.",
  badge = "Video Resources"
}) => {
  const [activeVideo, setActiveVideo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [channelInfo, setChannelInfo] = useState({ title: 'Amy C Brown Law', url: 'https://www.youtube.com/@amycbrownlaw' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fallback videos in case API fails
  const fallbackVideos = [
    {
      id: 'XthMPOpVWNo',
      title: 'Personal Injury Attorney - Amy C. Brown',
      thumbnail: 'https://img.youtube.com/vi/XthMPOpVWNo/maxresdefault.jpg'
    },
    {
      id: 'LEIJDYV6JOM',
      title: 'Maritime Injury & Jones Act Claims',
      thumbnail: 'https://img.youtube.com/vi/LEIJDYV6JOM/maxresdefault.jpg'
    },
    {
      id: 'ePUreJkODQo',
      title: 'Car Accident & Personal Injury Help',
      thumbnail: 'https://img.youtube.com/vi/ePUreJkODQo/maxresdefault.jpg'
    }
  ];

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/youtube/videos?max_results=6`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch videos');
        }
        
        const data = await response.json();
        
        if (data.videos && data.videos.length > 0) {
          setVideos(data.videos);
          setChannelInfo({
            title: data.channelTitle,
            url: data.channelUrl
          });
        } else {
          // Use fallback if no videos returned
          setVideos(fallbackVideos);
        }
      } catch (err) {
        console.error('Error fetching YouTube videos:', err);
        setError(err.message);
        // Use fallback videos on error
        setVideos(fallbackVideos);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const formatViewCount = (count) => {
    if (!count) return null;
    const num = parseInt(count, 10);
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M views`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K views`;
    }
    return `${num} views`;
  };

  return (
    <section className="py-20 bg-slate-50" data-testid="video-section">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Youtube className="w-4 h-4" />
            {badge}
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
            {title}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
            <span className="ml-3 text-slate-600">Loading videos...</span>
          </div>
        )}

        {/* Featured Video Player */}
        {activeVideo && (
          <div className="mb-12">
            <div className="relative aspect-video max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1&rel=0`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              ></iframe>
            </div>
            <button 
              onClick={() => setActiveVideo(null)}
              className="mt-4 mx-auto block text-slate-500 hover:text-slate-700 text-sm"
            >
              Close video
            </button>
          </div>
        )}

        {/* Video Grid */}
        {!loading && (
          <div className="grid md:grid-cols-3 gap-8">
            {videos.slice(0, 6).map((video) => (
              <div 
                key={video.id}
                className="group cursor-pointer"
                onClick={() => setActiveVideo(video.id)}
                data-testid={`video-card-${video.id}`}
              >
                <div className="relative aspect-video rounded-xl overflow-hidden shadow-lg mb-4">
                  <img 
                    src={video.thumbnail || `https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                    alt={video.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                    }}
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                      <Play className="w-8 h-8 text-white ml-1" fill="white" />
                    </div>
                  </div>
                  {activeVideo === video.id && (
                    <div className="absolute top-3 right-3 bg-red-600 text-white text-xs px-2 py-1 rounded-full">
                      Now Playing
                    </div>
                  )}
                </div>
                <h3 className="font-heading text-lg font-semibold text-secondary group-hover:text-teal-600 transition-colors line-clamp-2">
                  {video.title}
                </h3>
                {video.viewCount && (
                  <p className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                    <Eye className="w-4 h-4" />
                    {formatViewCount(video.viewCount)}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* YouTube Channel Link */}
        <div className="text-center mt-12">
          <a 
            href={channelInfo.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-full font-medium hover:bg-red-700 transition-colors"
            data-testid="youtube-channel-link"
          >
            <Youtube className="w-5 h-5" />
            Subscribe to My Channel
          </a>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
